import React, { useState } from 'react';
import CustomButton from './CustomButton';
import { Box, Typography } from '@mui/material';
import { useRegister } from '../context/RegisterContext';
import './StepExplicacao.css';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

interface ExplanationItem {
  step: number;
  title: string;
  description: string;
}

interface BenefitItem {
  text: string;
  bgColor: string;
  textColor: string;
}

const explanationList: ExplanationItem[] = [
  {
    step: 1,
    title: 'Nós atraímos o tutor',
    description: 'Investimos em marketing e aquisição. O tutor encontra seu pet shop pelo app e agenda o banho.',
  },
  {
    step: 2,
    title: 'Você realiza o banho',
    description: 'O cliente chega já com pagamento garantido pelo plano. Fluxo organizado, previsível e sem inadimplência.',
  },
  {
    step: 3,
    title: 'Você vende o que dá margem',
    description: 'Hidratação, corte de unhas, limpeza de ouvido, produtos, acessórios. É aqui que está o verdadeiro lucro.',
  },
];

const benefits: BenefitItem[] = [
  { text: '👥 Clientes recorrentes', bgColor: '#dbeafe', textColor: '#1e40af' },
  { text: '✨ Mais serviços por visita', bgColor: '#dcfce7', textColor: '#166534' },
  { text: '📈 Receita previsível', bgColor: '#f3e8ff', textColor: '#7c3aed' },
];

const StepExplicacao: React.FC<Props> = ({ onNext, onBack }) => {
  const { formData, updateExplicacao } = useRegister();
  const [checked, setChecked] = useState(formData.explicacao.entendi);
  const [error, setError] = useState('');

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked);
    setError('');
    updateExplicacao({ entendi: e.target.checked });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!checked) {
      setError('Você precisa confirmar que entendeu o fluxo.');
      return;
    }
    onNext();
  };

  return (
    <form className="step-explicacao" onSubmit={handleSubmit}>
      <Typography
        component="h1"
        sx={{
          fontSize: 'clamp(1.5rem, 3vw, 1.8rem)',
          fontWeight: 'bold',
          lineHeight: 1.1,
          marginBottom: '24px',
          letterSpacing: '-0.025em',
          textAlign: 'center',
        }}
      >
        No Clube do Banho,
        <Typography
          component="span"
          color="#2563eb"
          sx={{ fontWeight: 'bold', fontSize: 'clamp(14px, 1.6rem, 1.9rem)' }}
        >
          &nbsp;o banho é só o começo 🐶✨
        </Typography>
      </Typography>

      <Typography sx={{ color: '#64748b', marginTop: '2rem', marginBottom: '1.5rem', textAlign: 'center' }}>
        Você recebe tutores recorrentes e vende os serviços que realmente dão margem.
        <Typography component="span" sx={{ fontWeight: 'bold', color: 'black' }}>
          {' '}Nós levamos os clientes para a sua loja — você só precisa atendê-los.
        </Typography>
        {' '}
        <Typography component="span" sx={{ color: '#2563eb', fontWeight: 'bold' }}>
          O lucro está no que vem depois.
        </Typography>
      </Typography>

      <Box sx={{ marginTop: '32px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px' }}>
        {benefits.map(benefit => (
          <Box
            key={benefit.text}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 16px',
              backgroundColor: benefit.bgColor,
              color: benefit.textColor,
              borderRadius: '9999px',
            }}
          >
            <Typography component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
              {benefit.text}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        component="h2"
        sx={{ fontWeight: 'bold', fontSize: '2rem', marginBottom: '8px', marginTop: '3.5rem', textAlign: 'center' }}
      >
        Como funciona na prática
      </Typography>
      <p className="step-explicacao-subtitle">
        Um processo simples que transforma novos tutores em clientes fiéis do seu pet shop.
      </p>

      <div className="flex flex-col gap-5 mb-8">
        {explanationList.map(explanation => (
          <div
            key={explanation.step}
            className="flex justify-start items-center bg-white border border-[#e2e8f0] py-6 px-4 shadow-md rounded-2xl gap-4"
          >
            <div className="flex justify-center items-center bg-black p-5 rounded-full h-4 w-4 text-white font-bold">
              {explanation.step}
            </div>
            <div className="flex flex-col justify-center items-start">
              <h3 className="text-base font-bold mb-1">{explanation.title}</h3>
              <p className="text-sm text-[#64748b] font-normal">{explanation.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="upsell-box">
        <h3 className="upsell-title">Venda mais durante o atendimento</h3>
        <p className="upsell-text">
          Durante o atendimento, ofereça serviços extras como hidratação, corte de unhas. Tudo é
          registrado e pago automaticamente.
        </p>
        <p className="upsell-text-highlight">Demanda previsível sem gastar com anúncios.</p>
      </div>

      <div className="confirmation-checkbox">
        <label className="checkbox-label">
          <input type="checkbox" checked={checked} onChange={handleCheckboxChange} />
          <span className="checkbox-text">Entendi como funciona</span>
        </label>
        {error && <p className="error-message">{error}</p>}
      </div>

      <div className="form-actions form-actions-two">
        <CustomButton
          type="button"
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#FF914D',
            color: '#FF914D',
            width: '150px',
            '&:hover': {
              borderColor: '#FF914D',
              backgroundColor: 'rgba(255, 145, 77, 0.04)',
              color: '#FF914D',
            },
          }}
        >
          Voltar
        </CustomButton>
        <CustomButton type="submit" variant="contained" customBackground="#46558D" sx={{ width: '150px' }}>
          Continuar
        </CustomButton>
      </div>
    </form>
  );
};

export default StepExplicacao;
